import {
  fetchCategoryAncestry,
  fetchCategoryTree,
  getCategoryTree as repoGetCategoryTree,
} from "@/apps/shared/catalog/core/infrastructure/repository/categoryTreeRepository";
import { Category } from "@/apps/shared/catalog/core/domain/Category/Category";
import { MinimalCategory } from "@/apps/shared/catalog/core/domain/Category/MinimalCategory";

type CategoryTreeSelect = {
  id: string;
  label: string;
  category: Category;
  children?: CategoryTreeSelect[] | null;
};

export const getCategoryTree = async (): Promise<Category | null> => {
  const categoryTree = await repoGetCategoryTree();
  if (categoryTree) {
    return categoryTree;
  }

  await fetchCategoryTree();

  return await repoGetCategoryTree();
};

export const getCategoryTreeSelect = (category: Category): CategoryTreeSelect => {
  const mappedCategory: CategoryTreeSelect = {
    id: category.id,
    label: category.label,
    category,
  };

  if (!category.hasChildren) {
    return mappedCategory;
  }

  if (category.children?.length) {
    mappedCategory.children = category.children?.map((c) => getCategoryTreeSelect(c));
  } else if (category.hasChildren) {
    mappedCategory.children = null;
  }

  return mappedCategory;
};

export const loadDirectChildren = async (treeNode: {
  children: CategoryTreeSelect[] | null;
  category: Category;
}): Promise<void> => {
  await fetchCategoryTree(treeNode.category);

  treeNode.children = treeNode.category.children?.map((c) => getCategoryTreeSelect(c)) ?? null;
};

export const getCategoryAncestryWithoutRoot = async (category: Category): Promise<MinimalCategory[]> =>
  (await fetchCategoryAncestry(category)).slice(1, -1);

export const getCategories = async (): Promise<Category[]> => {
  const categoryTree = await getCategoryTree();
  if (!categoryTree) {
    return [];
  }

  return flattenCategoryTree(categoryTree);
};

const flattenCategoryTree = (category: Category): Category[] => {
  if (!category.children) {
    return [category];
  }

  return [
    category,
    ...category.children.flatMap((child: Category) => {
      return flattenCategoryTree(child);
    }),
  ];
};
